// [목적] AI 자연어 보정(refine, C5) API 클라이언트 — POST /folios/:id/refine(제안) +
//        POST /folios/:id/refine/:proposalId/apply(적용)
// [이유] FlowLine dcc2c3cc FE 과업1 — packages/types/src/refine.ts 에 BE/FE 공유 zod 스키마가 이미
//        존재하므로(rag.client.ts 의 "BE 전용 스키마 로컬 미러" 관례와 달리) 그대로 import 해 재정의를
//        피한다(ponytail — 이미 있는 스키마를 재사용). 컨트롤러 계약: apps/api/src/modules/rag/controllers/
//        folio-refine.controller.ts propose()/apply().
// [사이드 이팩트] fetchWithEnvelope 가 인증/CSRF 헤더를 자동 첨부 — 두 엔드포인트 모두 JwtAuthGuard+CsrfGuard.
// [수정 시 고려사항] propose 는 사용자 10/min RateLimit(429) — 호출부가 ApiError.status===429 를 처리해야 함.
//                   apply 의 proposalId 는 propose 응답의 proposalId 를 그대로 사용(Redis TTL 30분 만료 시
//                   404 rag.REFINE_PROPOSAL_NOT_FOUND).

import {
  refineProposalResponseSchema,
  refineApplyResponseSchema,
  type RefineScope,
  type RefineProposalResponse,
  type RefineApplyResponse,
} from '@gangwoncanvas/types';

import { fetchWithEnvelope } from './http.js';

/**
 * [목적] POST /folios/:id/refine — 자연어 보정 지시 → Day 변경 제안(미리보기, before/after) 생성
 * [이유] FlowLine dcc2c3cc: 사용자가 입력한 instruction + scope(Day/항목 단위)로 LLM 제안을 받아
 *        적용 전 미리보기를 렌더하기 위함.
 * [사이드 이팩트] BE 가 Redis 에 proposalId 키로 제안을 30분 TTL 저장(apply 시 참조).
 * [수정 시 고려사항] scope 는 현재 FE 가 { type:'day', dayIndex } 만 생성(item 단위 UI 는 이번 Wave 범위 밖).
 */
export async function proposeRefine(
  folioId: string,
  instruction: string,
  scope: RefineScope,
): Promise<RefineProposalResponse> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/refine`, {
    method: 'POST',
    body: JSON.stringify({ instruction, scope }),
  });
  return refineProposalResponseSchema.parse(data);
}

/**
 * [목적] POST /folios/:id/refine/:proposalId/apply — 제안을 day_plans/plan_items 에 트랜잭션 반영
 * [이유] 미리보기에서 사용자가 "적용"을 눌렀을 때 실제 폴리오 데이터를 갱신.
 * [사이드 이팩트] 성공 시 folio 캐시(queryKeys.folio)를 반드시 invalidate 해야 뷰에 반영됨(호출부 책임,
 *                useFolioRefine 훅이 처리).
 * [수정 시 고려사항] proposalId 만료/미존재/타 사용자 제안이면 404 rag.REFINE_PROPOSAL_NOT_FOUND
 *                   (존재 은닉 — "제안이 만료되었어요, 다시 요청해 주세요" 안내로 통일).
 */
export async function applyRefineProposal(
  folioId: string,
  proposalId: string,
): Promise<RefineApplyResponse> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/refine/${proposalId}/apply`, {
    method: 'POST',
  });
  return refineApplyResponseSchema.parse(data);
}
